import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Star, Bell, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TemplateGrid } from "@/components/TemplateCard";
import { Spinner, EmptyState, PageHeader, FormatBadge, StatusBadge } from "@/components/common";
import { ReviewForm } from "@/pages/TemplateDetail";
import { api, media, naira, fmtDate, fmtDateTime, errMsg, downloadTemplate } from "@/lib/api";

const Wrap = ({ children }) => <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">{children}</div>;

export function Purchases() {
  const [items, setItems] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const load = () => api.get("/purchases").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const dl = async (tid) => { try { const f = await downloadTemplate(tid); toast.success(`Downloading ${f}`); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Wrap>
      <PageHeader eyebrow="Library" title="My Purchases" />
      {!items ? <Spinner /> : items.length === 0 ? (
        <EmptyState title="No purchases yet" text="Templates you buy appear here, ready to download." action={<Button asChild><Link to="/explore">Browse templates</Link></Button>} testid="purchases-empty" />
      ) : (
        <div className="grid gap-4">
          {items.map((i) => (
            <div key={i.id} className="card-surface flex flex-col gap-4 p-4 sm:flex-row sm:items-center" data-testid={`purchase-${i.template_id}`}>
              <Link to={`/template/${i.template_id}`} className="flex min-w-0 flex-1 items-center gap-4">
                <img src={media(i.cover_image)} alt="" className="h-20 w-16 shrink-0 rounded-lg object-cover" />
                <div className="min-w-0">
                  <p className="truncate font-semibold">{i.title}</p>
                  <p className="text-xs text-muted-foreground">By {i.seller_name} · {fmtDate(i.paid_at)} · {naira(i.price)}</p>
                  <div className="mt-1.5 flex gap-1">{(i.file_formats || []).map((f) => <FormatBadge key={f} f={f} />)}</div>
                </div>
              </Link>
              <div className="flex gap-2">
                {!i.reviewed && <Button variant="outline" size="sm" onClick={() => setReviewFor(i)} data-testid={`review-btn-${i.template_id}`}><Star size={14} className="mr-1.5" />Review</Button>}
                <Button size="sm" onClick={() => dl(i.template_id)} data-testid={`download-btn-${i.template_id}`}><Download size={14} className="mr-1.5" />Download</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Review {reviewFor?.title}</DialogTitle></DialogHeader>
          {reviewFor && <ReviewForm tid={reviewFor.template_id} onDone={() => { setReviewFor(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </Wrap>
  );
}

export function Favorites() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/favorites").then((r) => setItems(r.data)); }, []);
  return (
    <Wrap>
      <PageHeader eyebrow="Saved" title="My Favorites" />
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState title="No favorites yet" text="Tap the heart on any template to save it here." testid="favorites-empty" /> : <TemplateGrid items={items} />}
    </Wrap>
  );
}

export function Orders() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/orders").then((r) => setItems(r.data)); }, []);
  return (
    <Wrap>
      <PageHeader eyebrow="History" title="Order history" />
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState title="No orders yet" testid="orders-empty" /> : (
        <div className="card-surface divide-y divide-border">
          {items.map((o) => (
            <div key={o.id} className="flex flex-wrap items-center gap-3 p-4 text-sm" data-testid={`order-${o.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{o.items.map((i) => i.title).join(", ")}</p>
                <p className="font-mono-ui text-xs text-muted-foreground">{o.order_ref} · {fmtDateTime(o.created_at)} · {o.provider}</p>
              </div>
              <span className="font-semibold">{naira(o.total)}</span>
              <StatusBadge s={o.status} />
            </div>
          ))}
        </div>
      )}
    </Wrap>
  );
}

export function Notifications() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  const load = () => api.get("/notifications").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <Wrap>
      <PageHeader eyebrow="Inbox" title="Notifications">
        <Button variant="outline" size="sm" onClick={readAll} data-testid="mark-all-read-btn"><CheckCheck size={15} className="mr-2" />Mark all read</Button>
      </PageHeader>
      {!data ? <Spinner /> : data.items.length === 0 ? <EmptyState title="You're all caught up" testid="notifications-empty" /> : (
        <div className="card-surface divide-y divide-border">
          {data.items.map((n) => (
            <button key={n.id} onClick={() => n.link && nav(n.link)} data-testid={`notification-${n.id}`} className="flex w-full items-start gap-3 p-4 text-left hover:bg-secondary/50">
              <span className={`mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-full ${n.read ? "bg-secondary" : "bg-primary/15 text-primary"}`}><Bell size={14} /></span>
              <div className="min-w-0 flex-1"><p className="text-sm font-semibold">{n.title}</p><p className="text-sm text-muted-foreground">{n.message}</p></div>
              <span className="shrink-0 text-xs text-muted-foreground">{fmtDateTime(n.created_at)}</span>
            </button>
          ))}
        </div>
      )}
    </Wrap>
  );
}
